/**
 * Metadados e agrupamento de eventos de automação (regras_automacao_condicoes).
 * Complementa o catálogo da API com copy amigável e categorias para a UI.
 */

export const EVENT_GROUPS = [
  {
    id: "entrada",
    label: "Entrada e criação",
    description: "Quando uma solicitação nasce ou chega à plataforma.",
    order: 1,
  },
  {
    id: "alteracao",
    label: "Alterações na solicitação",
    description: "Quando campos ou etapas da solicitação são modificados.",
    order: 2,
  },
  {
    id: "tempo",
    label: "Tempo e recorrência",
    description: "Não percorrem conversas antigas. O prazo pode atrasar até cerca de 1 minuto.",
    order: 3,
  },
  {
    id: "webchat",
    label: "Web Chat",
    description: "Sessão e mensagens do chat no site ou do link WhatsApp → Web Chat.",
    order: 4,
  },
  {
    id: "chatbot",
    label: "ChatBot",
    description: "Acionadas em passos específicos do fluxo conversacional.",
    order: 5,
  },
  {
    id: "outros",
    label: "Outros eventos",
    description: "Demais gatilhos disponíveis neste workspace.",
    order: 99,
  },
];

/** @typedef {'pontual'|'recorrente'|'preventivo'} EventTimingKind */

const BY_ID = {
  2: {
    group: "entrada",
    timing: "pontual",
    shortTitle: "Nova solicitação",
    description:
      "Dispara quando uma solicitação é criada — manualmente, por integração, e-commerce ou canal de atendimento.",
    example: "Enviar e-mail de boas-vindas assim que o lead entrar.",
  },
  6: {
    group: "alteracao",
    timing: "pontual",
    shortTitle: "Após alterar solicitação",
    description:
      "Executa depois que a alteração já foi salva. Ideal para notificações, atualizações e ações que não precisam impedir a mudança.",
    example: "Abrir pesquisa na tela ou enviar e-mail após mudança de status.",
  },
  30: {
    group: "entrada",
    timing: "pontual",
    shortTitle: "Pedido e-commerce atualizado",
    description: "Dispara em eventos do fluxo de pedido online vinculado à solicitação.",
    example: "Notificar equipe quando o carrinho virar pedido.",
  },
  31: {
    group: "entrada",
    timing: "pontual",
    shortTitle: "Pagamento confirmado",
    description: "Executa quando um pagamento vinculado à solicitação é confirmado.",
    example: "Alterar status ou enviar comprovante após confirmação.",
  },
  42: {
    group: "entrada",
    timing: "pontual",
    shortTitle: "Pagamento recusado",
    description:
      "Executa quando o pagamento do pedido é recusado ou cancelado pelo emissor (ou reprovado no antifraude).",
    example: "Enviar e-mail ao cliente oferecendo nova tentativa de pagamento.",
  },
  43: {
    group: "entrada",
    timing: "pontual",
    shortTitle: "Pagamento não feito (Pix expirado)",
    description:
      "Dispara quando um Pix foi gerado mas não foi pago até o vencimento do código. " +
      "Use para finalizar pedidos abandonados, mudar etapa ou notificar a equipe — " +
      "sem depender de regras genéricas por tempo sem movimentação. " +
      "Não dispara enquanto o Pix ainda está dentro do prazo, nem quando o banco recusa o pagamento (use Pagamento recusado).",
    example: "Finalizar solicitações com Pix vencido e remover da fila de Em atendimento.",
  },
  32: {
    group: "alteracao",
    timing: "pontual",
    shortTitle: "Nota fiscal atualizada",
    description: "Dispara quando o campo de NF da solicitação é alterado.",
    example: "Avisar financeiro ao registrar a NF.",
  },
  33: {
    group: "alteracao",
    timing: "pontual",
    shortTitle: "Após mudar etapa do funil",
    description: "Executa depois que a solicitação muda de etapa no funil de vendas.",
    example: "Enviar mensagem automática ao avançar para proposta.",
  },
  34: {
    group: "alteracao",
    timing: "preventivo",
    shortTitle: "Antes de mudar etapa do funil",
    description:
      "Avalia a regra antes da mudança de etapa. Pode bloquear a ação se houver regra com “Bloquear ação”.",
    example: "Impedir avanço sem campos obrigatórios preenchidos.",
  },
  36: {
    group: "chatbot",
    timing: "pontual",
    shortTitle: "Acionado pelo ChatBot",
    description:
      "Não dispara sozinha: vincule esta regra em um passo do ChatBot (ação “Regras de automação”). Alterações na regra refletem em todos os passos que a usam.",
    example: "Executar fluxo de qualificação após o cliente escolher uma opção.",
  },
  37: {
    group: "alteracao",
    timing: "preventivo",
    shortTitle: "Antes de alterar solicitação",
    description:
      "Avalia filtros antes de salvar a alteração. Use com “Bloquear ação” para impedir mudanças inválidas.",
    example: "Bloquear conclusão sem pesquisa respondida.",
  },
  38: {
    group: "alteracao",
    timing: "pontual",
    shortTitle: "Após mudar nível de consciência",
    description:
      "Dispara quando o cliente muda de estágio mental na jornada (níveis de consciência Schwartz).",
    example: "Enviar conteúdo educativo ao mover para topo de funil.",
  },
  45: {
    group: "webchat",
    timing: "pontual",
    shortTitle: "Sessão de Web Chat iniciada",
    description:
      "Dispara na primeira abertura da sessão — widget no site ou link WhatsApp → Web Chat. Combine com qualquer ação: WhatsApp, e-mail, convite, alteração de status.",
    example: "Notificar o time quando o cliente abrir o chat no site.",
  },
  46: {
    group: "webchat",
    timing: "pontual",
    shortTitle: "Mensagem recebida no Web Chat",
    description:
      "Dispara quando o cliente envia uma mensagem no Web Chat. Não dispara em envios automáticos da própria regra.",
    example: "Alterar status para Pendente ou notificar o responsável.",
  },
  47: {
    group: "webchat",
    timing: "pontual",
    shortTitle: "Mensagem enviada no Web Chat",
    description:
      "Dispara quando a equipe, o ChatBot ou o agente responde no Web Chat. Use para seguir o fluxo depois do retorno.",
    example: "Marcar a solicitação como Em atendimento.",
  },
  48: {
    group: "tempo",
    timing: "recorrente",
    shortTitle: "Cliente sem retorno após atendimento há X tempo",
    description:
      "Conta o tempo desde a última mensagem da equipe (humano, ChatBot ou agente), sem resposta posterior do cliente. Não usa abertura de aba nem envio automático. Combine com origem Web Chat, WhatsApp ou o filtro Web Chat ativo. Várias regras no mesmo evento podem ter prazos e ações diferentes.",
    example:
      "30 minutos sem retorno: enviar convite de Web Chat no WhatsApp. 6 horas: encerrar a solicitação.",
  },
};

const TIMING_LABELS = {
  pontual: "Pontual",
  recorrente: "Recorrente",
  preventivo: "Preventivo",
};

const TIMING_HINTS = {
  pontual: "Só a conversa que disparou o evento. Criar a regra não mexe nas que já existem.",
  recorrente: "Não olha o passado. A checagem roda cerca de 1 vez por minuto.",
  preventivo: "Pode impedir a ação antes de ela ser concluída.",
};

function getEventForm(evento) {
  return evento?.form?.condicoes || evento?.form?.fields || evento?.form || {};
}

function matchName(nome, patterns) {
  const n = String(nome || "").toLowerCase();
  return patterns.some((p) => n.includes(p));
}

function classifyByName(nome, hasSchedule) {
  if (matchName(nome, ["chatbot"])) {
    return {
      group: "chatbot",
      timing: "pontual",
      shortTitle: "Acionado pelo ChatBot",
      description: BY_ID[36].description,
      example: BY_ID[36].example,
    };
  }

  if (matchName(nome, ["antes de alterar"])) {
    return {
      group: "alteracao",
      timing: "preventivo",
      shortTitle: "Antes de alterar solicitação",
      description: BY_ID[37].description,
      example: BY_ID[37].example,
    };
  }

  if (matchName(nome, ["ao alterar", "alterar solicitação"])) {
    return {
      group: "alteracao",
      timing: "pontual",
      shortTitle: "Após alterar solicitação",
      description: BY_ID[6].description,
      example: BY_ID[6].example,
    };
  }

  if (matchName(nome, ["sessão de web", "sessao de web", "web chat iniciada"])) {
    return BY_ID[45];
  }

  if (matchName(nome, ["mensagem recebida no web"])) {
    return BY_ID[46];
  }

  if (matchName(nome, ["mensagem enviada no web"])) {
    return BY_ID[47];
  }

  if (hasSchedule && matchName(nome, ["sem retorno", "após atendimento", "apos atendimento"])) {
    return BY_ID[48];
  }

  if (hasSchedule && matchName(nome, ["intervalo", "a cada"])) {
    return {
      group: "tempo",
      timing: "recorrente",
      shortTitle: "A cada intervalo de tempo",
      description:
        "Repete no intervalo que você definir, só em conversas criadas depois que a regra for ligada. Conversas que já existiam ficam de fora. O primeiro ciclo espera o intervalo inteiro. A checagem pode atrasar até 1 minuto.",
      example: "A cada 30 minutos, avisar o responsável das conversas novas que continuam abertas. Não mexe nas de ontem.",
    };
  }

  if (hasSchedule && matchName(nome, ["sem resposta"])) {
    return {
      group: "tempo",
      timing: "recorrente",
      shortTitle: "Solicitação sem resposta há X tempo",
      description:
        "Conta o tempo desde a criação, e só enquanto o status está Pendente. Não usa a data da última mensagem. Só entra solicitação criada nos últimos 20 minutos. Quem já estava sem resposta fica de fora. Se o atraso passar de 15 minutos além do prazo, não dispara.",
      example: "15 minutos Pendente desde a abertura: avisar o time. Solicitações antigas sem resposta não entram.",
    };
  }

  if (hasSchedule && matchName(nome, ["movimentação", "inatividade", "sem moviment"])) {
    return {
      group: "tempo",
      timing: "recorrente",
      shortTitle: "Sem movimentação há X tempo",
      description:
        "Dispara depois do tempo sem atualização e sem mensagem nova. Só entra conversa mexida nos últimos 20 minutos. Silêncio mais antigo que o prazo mais 15 minutos não dispara. Uma mensagem nova zera a contagem.",
      example: "5 minutos sem mensagem: enviar um WhatsApp. Quem está parado há dias não recebe.",
    };
  }

  if (matchName(nome, ["ctwa", "anúncio", "anuncio"])) {
    return {
      group: "entrada",
      timing: "pontual",
      shortTitle: "WhatsApp via anúncio (CTWA)",
      description: "Dispara quando o cliente inicia conversa pelo anúncio Click-to-WhatsApp.",
      example: "Priorizar atendimento de leads vindos de campanha paga.",
    };
  }

  if (matchName(nome, ["nova", "criad", "criação", "criacao"])) {
    return {
      group: "entrada",
      timing: "pontual",
      shortTitle: nome,
      description: "Dispara na criação ou entrada de uma nova solicitação.",
      example: "Automatizar boas-vindas ou distribuição inicial.",
    };
  }

  if (matchName(nome, ["nf", "nota fiscal"])) {
    return BY_ID[32];
  }

  if (matchName(nome, ["funil", "pipeline", "etapa"])) {
    if (matchName(nome, ["antes"])) return BY_ID[34];
    return BY_ID[33];
  }

  if (matchName(nome, ["pagamento", "payment"])) {
    if (matchName(nome, ["recus", "recusado", "cancel", "recusada", "negad", "declin"])) {
      return BY_ID[42];
    }
    return BY_ID[31];
  }

  return null;
}

export function enrichAutomationEvent(evento) {
  const form = getEventForm(evento);
  const hasSchedule = Boolean(form?.schedule_interval);
  const id = Number(evento?.id);
  const nome = String(evento?.nome || "").trim();

  const meta =
    BY_ID[id] ||
    classifyByName(nome, hasSchedule) || {
      group: hasSchedule ? "tempo" : "outros",
      timing: hasSchedule ? "recorrente" : "pontual",
      shortTitle: nome || `Evento #${id}`,
      description:
        evento?.descricao ||
        evento?.help ||
        (hasSchedule
          ? "Regra de tempo. Não percorre conversas antigas. A checagem pode atrasar até 1 minuto."
          : "Configure filtros no próximo passo para limitar quando a regra deve disparar."),
      example: null,
    };

  const groupDef = EVENT_GROUPS.find((g) => g.id === meta.group) || EVENT_GROUPS.find((g) => g.id === "outros");

  return {
    ...evento,
    meta: {
      ...meta,
      groupLabel: groupDef?.label || "Outros",
      timingLabel: TIMING_LABELS[meta.timing] || TIMING_LABELS.pontual,
      timingHint: TIMING_HINTS[meta.timing] || TIMING_HINTS.pontual,
      displayTitle: meta.shortTitle || nome,
    },
  };
}

export function groupAutomationEvents(eventos = [], search = "") {
  const q = String(search || "")
    .trim()
    .toLowerCase();

  const enriched = (eventos || []).map(enrichAutomationEvent);

  const filtered = q
    ? enriched.filter((evt) => {
        const hay = [
          evt.nome,
          evt.meta.displayTitle,
          evt.meta.description,
          evt.meta.example,
          evt.meta.groupLabel,
          evt.meta.timingLabel,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
    : enriched;

  const grouped = new Map();
  for (const evt of filtered) {
    const gid = evt.meta.group || "outros";
    if (!grouped.has(gid)) grouped.set(gid, []);
    grouped.get(gid).push(evt);
  }

  for (const [, list] of grouped) {
    list.sort((a, b) => {
      const ta = String(a.meta.displayTitle || a.nome || "");
      const tb = String(b.meta.displayTitle || b.nome || "");
      return ta.localeCompare(tb, "pt-BR");
    });
  }

  return EVENT_GROUPS.filter((g) => grouped.has(g.id)).map((g) => ({
    ...g,
    events: grouped.get(g.id) || [],
  }));
}

export function findEnrichedEvent(eventos, eventId) {
  const id = Number(eventId);
  if (!id) return null;
  const raw = (eventos || []).find((e) => Number(e.id) === id);
  return raw ? enrichAutomationEvent(raw) : null;
}
